import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Download, 
  Share, 
  PlusSquare, 
  MoreVertical, 
  Smartphone,
  CheckCircle,
  ArrowRight,
  Apple,
  Chrome
} from 'lucide-react';

const Install = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();

  const handleInstallClick = async () => {
    if (isInstallable) {
      await promptInstall();
    }
  };

  const handleStartClick = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LandingHeader />
      
      <main className="flex-1 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Smartphone className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-4xl font-bold mb-4">{t('install.title')}</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('install.subtitle')}
            </p>

            {/* Quick Install Button (for supported browsers) */}
            {isInstalled ? (
              <div className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-primary/10 text-primary rounded-full">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">{t('install.alreadyInstalled')}</span>
              </div>
            ) : isInstallable ? (
              <Button size="lg" className="mt-8 gap-2" onClick={handleInstallClick}>
                <Download className="w-5 h-5" />
                {t('install.installNow')}
              </Button>
            ) : null}
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {['offline', 'fast', 'native'].map((benefit) => (
              <Card key={benefit} className="text-center">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{t(`install.benefits.${benefit}.title`)}</h3>
                  <p className="text-sm text-muted-foreground">{t(`install.benefits.${benefit}.desc`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Separator className="my-12" />

          {/* iOS Instructions */}
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                  <Apple className="w-7 h-7 text-white" />
                </div>
                <div>
                  <CardTitle>{t('install.ios.title')}</CardTitle>
                  <CardDescription>{t('install.ios.subtitle')}</CardDescription>
                </div>
                <Badge variant="secondary" className="ml-auto">Safari</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ol className="space-y-6">
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="font-medium mb-2">{t('install.ios.step1.title')}</p>
                    <p className="text-sm text-muted-foreground">{t('install.ios.step1.desc')}</p>
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                      <Share className="w-5 h-5 text-blue-500" />
                      <span className="text-sm">{t('install.ios.step1.button')}</span>
                    </div>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="font-medium mb-2">{t('install.ios.step2.title')}</p>
                    <p className="text-sm text-muted-foreground">{t('install.ios.step2.desc')}</p>
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                      <PlusSquare className="w-5 h-5" />
                      <span className="text-sm">{t('install.ios.step2.button')}</span>
                    </div>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div className="flex-1">
                    <p className="font-medium mb-2">{t('install.ios.step3.title')}</p>
                    <p className="text-sm text-muted-foreground">{t('install.ios.step3.desc')}</p>
                  </div>
                </li>
              </ol>
            </CardContent>
          </Card>

          {/* Android Instructions */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                  <Chrome className="w-7 h-7 text-white" />
                </div>
                <div>
                  <CardTitle>{t('install.android.title')}</CardTitle>
                  <CardDescription>{t('install.android.subtitle')}</CardDescription>
                </div>
                <Badge variant="secondary" className="ml-auto">Chrome</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ol className="space-y-6">
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="font-medium mb-2">{t('install.android.step1.title')}</p>
                    <p className="text-sm text-muted-foreground">{t('install.android.step1.desc')}</p>
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                      <MoreVertical className="w-5 h-5" />
                      <span className="text-sm">{t('install.android.step1.button')}</span>
                    </div>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="font-medium mb-2">{t('install.android.step2.title')}</p>
                    <p className="text-sm text-muted-foreground">{t('install.android.step2.desc')}</p>
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                      <Download className="w-5 h-5" />
                      <span className="text-sm">{t('install.android.step2.button')}</span>
                    </div>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div className="flex-1">
                    <p className="font-medium mb-2">{t('install.android.step3.title')}</p>
                    <p className="text-sm text-muted-foreground">{t('install.android.step3.desc')}</p>
                  </div>
                </li>
              </ol>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <p className="text-muted-foreground mb-4">{t('install.readyToStart')}</p>
            <Button onClick={handleStartClick} size="lg" className="gap-2">
              {t('install.getStarted')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
};

export default Install;
